import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  UploadCloud,
  X,
  FileText,
  Trash2,
  Tractor,
  Loader2,
} from "lucide-react";
import { api } from "../api";
import { useUiStore } from "../stores/uiStore";
import { qk, useBulkSessions } from "../hooks/queries";
import { JdFlow } from "../features/bulk-jd/BulkJdFlow";
import { IdentificationCard } from "../features/drop-identification/DropIdentificationCard";
import { UploadSuccess } from "../features/upload-success/UploadSuccess";
import { fmtBytes } from "../lib/files";
import { DETECTED_CLASSES, classLabel } from "../lib/bulkIngest";
import {
  EMPTY_SAMPLE_DEFAULTS,
  sampleDefaultsProblem,
  toSampleDefaults,
  type SampleDefaultsDraft,
} from "../lib/sampleDefaults";
import { BulkSampleDefaults } from "../features/bulk-sample-defaults/BulkSampleDefaults";
import { BulkSessionView } from "./BulkSessionView";
import type { BoundaryScanResult, FileScan } from "../lib/boundaryScan";
import {
  createFieldsFast,
  MAX_FAST_CREATES,
  type FastPathReport,
} from "../lib/bulkFieldFastPath";
import type { SampleAttachReport } from "../lib/sampleFastPath";
import { cachedLayerRows } from "../lib/layerLoad";
import { targetAttachLine } from "../lib/targetPointLabel";
import type { TargetAttachReport } from "../lib/targetPointsLane";
import type {
  ZoneAttachReport,
  ZoneResultAttachReport,
} from "../lib/zoneFastPath";
import type { DropIdentification } from "../features/drop-identification/dropIdentify";
import { handoffFor } from "../features/drop-identification/dropOutcome";
import type { YieldNeedsInfo, YieldScanResult } from "../lib/yieldFastPath";
import { applyYieldCommit, yieldCommitShouldRetire } from "../lib/yieldCommit";
import { Modal } from "../ui/Modal";

// The upload half runs the `:begin` era (BE-Light rev 00106+): ONE call at press files the whole
// job chain (incl.

// The classes we tell the user we detect, rendered from the SHARED token vocabulary (lib/bulkIngest) rather than
// re-typed here — compute froze one token set FE→DB precisely to avoid a second vocabulary drifting from it.
const CLASS_LIST = DETECTED_CLASSES.map(classLabel).join(", ");

function TargetLaneNote({ report }: { report: TargetAttachReport | null }) {
  const qc = useQueryClient();
  if (!report) return null;
  const lines = report.attached.map((a) =>
    targetAttachLine(a, cachedLayerRows(qc, a.fieldId)),
  );
  if (
    !lines.length &&
    report.refused.length === 0 &&
    report.failed.length === 0
  )
    return null;
  return (
    <div className="rounded border border-emerald-500/40 bg-emerald-500/5 px-2 py-1.5 text-[11px]">
      {lines.map((line, i) => (
        <div key={`${line}:${i}`} className="font-medium text-fg">
          {line}
        </div>
      ))}
      {report.refused.length > 0 && (
        <div className="mt-0.5 text-[10px] text-red-400">
          {report.refused.map((r) => r.message).join(" · ")}
        </div>
      )}
      {report.failed.length > 0 && (
        <div className="mt-0.5 text-[10px] text-red-400">
          {report.failed.map((f) => `${f.file}: ${f.error}`).join(" · ")}
        </div>
      )}
    </div>
  );
}

function completePairPointKeys(pairing: {
  pairs: { complete: boolean; pointsKey: string; pointsKeys?: string[] }[];
}): Set<string> {
  const skip = new Set<string>();
  for (const pair of pairing.pairs) {
    if (!pair.complete) continue;
    skip.add(pair.pointsKey);
    for (const key of pair.pointsKeys ?? []) skip.add(key);
  }
  return skip;
}

function UploadFlow() {
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [phase, setPhase] = useState<
    | "idle"
    | "creating"
    | "attaching"
    | "yield"
    | "uploading"
    | "starting"
    | "started"
  >("idle");
  const [sessionId, setSessionId] = useState<string | null>(null);
  // Reopened from "Drops in progress" rather than started here — that session already exists server-side
  // so a not-found for it genuinely means not-found.
  const [resumed, setResumed] = useState(false);
  // Files that never reached GCS are INVISIBLE to the server — they aren't in the manifest we hand compute, so
  // they can never appear in `failed_file_count`. If we don't surface them here, they vanish silently.
  const [uploadFailed, setUploadFailed] = useState<
    { filename: string; error: string }[]
  >([]);
  const [done, setDone] = useState(0);
  // JD drops never come through here (their boundaries are server-fetched).
  const [scan, setScan] = useState<BoundaryScanResult | null>(null);
  const [scanning, setScanning] = useState(false);
  const [creating, setCreating] = useState<{
    done: number;
    total: number;
    last?: string;
  } | null>(null);
  const [fastReport, setFastReport] = useState<FastPathReport | null>(null);
  // SAMPLE FAST PATH: GPS-bearing lab CSVs attach to their CONTAINING fields at press time —
  // placement by location (org-checked server-side), never by a name the CSV claims.
  // `handledSampleFiles` persists across a:begin retry: those files were ATTACHED and must carry
  // their `fe_attached` annotation on the re-upload, or the server chain would ingest them again.
  const [attaching, setAttaching] = useState<{
    done: number;
    total: number;
    last?: string;
  } | null>(null);
  const [sampleReport, setSampleReport] = useState<SampleAttachReport | null>(
    null,
  );
  const [sampleLeft, setSampleLeft] = useState<
    { file: string; reason: string }[]
  >([]);
  const [targetReport, setTargetReport] = useState<TargetAttachReport | null>(
    null,
  );
  const [targetLeft, setTargetLeft] = useState<
    { file: string; reason: string }[]
  >([]);
  const [handledSampleFiles, setHandledSampleFiles] = useState<string[]>([]);
  const [zoneReport, setZoneReport] = useState<ZoneAttachReport | null>(null);
  const [zoneResultReport, setZoneResultReport] =
    useState<ZoneResultAttachReport | null>(null);
  const [zoneLeft, setZoneLeft] = useState<{ file: string; reason: string }[]>(
    [],
  );
  // THE SECOND SCREEN: pressing Upload & process leaves the manifest and shows WHAT WE IDENTIFIED —
  // boundaries, sample points with/without lab results, yield maps, EC maps zone sets, unidentified
  // each with the org fields it belongs to. Display-only classification (lib/dropIdentify); the
  // server chain's classifier still decides what actually happens.
  const [ident, setIdent] = useState<DropIdentification | null>(null);
  // THE YIELD LANE: harvest exports are parsed at press time and ATTESTED on the upload so
  // compute's narrow `attach_yield` lane can persist them in minutes instead of the ~30-40 the full
  // chain takes.
  const [yieldReport, setYieldReport] = useState<YieldScanResult | null>(null);
  // a sidecar-less export can't state its crop or year, and landing dateless makes it invisible to
  // every model — so we ASK and `run` waits on the answer.
  const [yieldPrompt, setYieldPrompt] = useState<YieldNeedsInfo[] | null>(null);
  const [yieldAnswers, setYieldAnswers] = useState<
    Record<string, { crop: string; year: string }>
  >({});
  const yieldResolveRef = useRef<
    ((a: Map<string, { crop: string; year: number }> | null) => void) | null
  >(null);
  /*
   * fileKeys whose `:begin` actually returned `yield_job_id` AND whose bytes uploaded. The
   * "minutes" card and `markAttested` both key off this — a scan is not a filing.
   */
  const [yieldFiledKeys, setYieldFiledKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [yieldOnMap, setYieldOnMap] = useState(false);
  const qc = useQueryClient();
  // per-FILE scan cache (name:size) — a drop gesture re-runs only the NEW files, not every archive.
  const scanCacheRef = useRef(new Map<string, FileScan>());
  const scanSeqRef = useRef(0);
  const scanPromiseRef = useRef<Promise<BoundaryScanResult | null>>(
    Promise.resolve(null),
  );
  useEffect(() => {
    const seq = ++scanSeqRef.current;
    if (!files.length) {
      setScan(null);
      setScanning(false);
      scanPromiseRef.current = Promise.resolve(null);
      return;
    }
    let alive = true;
    setScanning(true);
    setScan(null); // a stale result must never describe a manifest it wasn't scanned from (review).
    setIdent(null);
    setFastReport(null);
    setSampleReport(null);
    setSampleLeft([]);
    setTargetReport(null);
    setTargetLeft([]);
    setZoneReport(null);
    setZoneResultReport(null);
    setZoneLeft([]);
    setYieldReport(null);
    setYieldFiledKeys(new Set());
    setYieldOnMap(false);
    const p = (async () => {
      // lazy-loaded on purpose: the readers (and proj4's WKT machinery) stay OUT of the main chunk
      const { scanFile, combineScans, fileBytes } =
        await import("../lib/boundaryScan");
      const scans: FileScan[] = [];
      for (const f of files) {
        const key = `${f.name}:${f.size}`;
        let s = scanCacheRef.current.get(key);
        if (!s) {
          s = await scanFile({
            name: f.name,
            size: f.size,
            bytes: () => fileBytes(f),
          });
          scanCacheRef.current.set(key, s);
        }
        scans.push(s);
      }
      return combineScans(scans);
    })();
    scanPromiseRef.current = p.catch(() => null);
    void p
      .then((r) => {
        if (alive && seq === scanSeqRef.current) setScan(r);
      })
      .catch(() => {
        if (alive && seq === scanSeqRef.current) setScan(null);
      })
      .finally(() => {
        if (alive && seq === scanSeqRef.current) setScanning(false);
      });
    return () => {
      alive = false;
    };
  }, [files]);
  // Fallback sample metadata, REQUIRED before anything uploads. Deliberately not remembered between
  // drops: a remembered extractant is how the wrong one rides along into a lab's results that used
  // a different one.
  const [defaults, setDefaults] = useState<SampleDefaultsDraft>(
    EMPTY_SAMPLE_DEFAULTS,
  );
  // SAMPLE-INTENT gating: sample ingestion is EXPLICIT opt-in.
  const [maySamples, setMaySamples] = useState(false);
  const [matchByPointId, setMatchByPointId] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: recent } = useBulkSessions(
    phase === "idle" && files.length === 0,
  );

  // de-dupe by name+size so dropping the same batch twice doesn't double the manifest
  const addFiles = (incoming: FileList | null) => {
    if (!incoming?.length) return;
    setFiles((cur) => {
      const seen = new Set(cur.map((f) => `${f.name}:${f.size}`));
      const next = [...cur];
      for (const f of Array.from(incoming))
        if (!seen.has(`${f.name}:${f.size}`)) next.push(f);
      return next;
    });
  };
  const totalBytes = useMemo(
    () => files.reduce((n, f) => n + f.size, 0),
    [files],
  );
  const defaultsProblem = maySamples ? sampleDefaultsProblem(defaults) : null;

  const run = async () => {
    const seq = scanSeqRef.current;
    setErr(null);
    setUploadFailed([]);
    setDone(0);

    // A failure here never blocks the drop: the server chain creates whatever this step didn't.
    // never a STALE scan (review, wrong-field class: pressing during a re-scan used the previous
    // manifest's result — a field from a just-REMOVED file got created).
    setPhase("creating");
    const scanNow = files.length ? await scanPromiseRef.current : null;
    // a RETRY after a failed:begin keeps the allowlist (review: the second run re-found the first
    // run's fields as matchedExisting and sent an EMPTY allowlist — every one became a dedup error)
    const already = fastReport?.created.map((c) => c.field.field_id) ?? [];
    let feCreatedFieldIds: string[] | undefined = already.length
      ? [...already]
      : undefined;
    if (
      !fastReport &&
      scanNow?.candidates.length &&
      scanNow.candidates.length <= MAX_FAST_CREATES
    ) {
      setCreating({ done: 0, total: scanNow.candidates.length });
      try {
        const report = await createFieldsFast(scanNow.candidates, {
          onProgress: (d, total, last) => setCreating({ done: d, total, last }),
          // invalidate on ACTUAL creates only — skips change nothing (review)
          onCreated: () => void qc.invalidateQueries({ queryKey: qk.fields }),
        });
        setFastReport(report);
        feCreatedFieldIds = report.created.length
          ? report.created.map((c) => c.field.field_id)
          : undefined;
      } catch {
        /* the chain remains the creator of record */
      }
      setCreating(null);
    }
    // ⚡ SAMPLE FAST PATH — GPS CSVs, geo lab samples, GPS-only locations, in-drop pairs, and
    // (when Match by point ID is on) location-less results joined onto existing sample locations.
    const sfp = await import("../lib/sampleFastPath");
    const pairing = await import("../lib/samplePairing")
      .then((m) => m.pairDropSampleFiles(files))
      .catch(() => ({ pairs: [], notes: [], consumedKeys: [] as string[] }));
    const feAttachedKeys = new Set(handledSampleFiles);
    for (const f of files)
      if (sfp.sessionAttachedKeys().has(sfp.fileKeyOf(f)))
        feAttachedKeys.add(sfp.fileKeyOf(f));
    const zfp = await import("../lib/zoneFastPath");
    for (const k of zfp.sessionZoneAttachedKeys()) feAttachedKeys.add(k);
    const tpl = await import("../lib/targetPointsLane");
    for (const k of tpl.sessionTargetAttachedKeys()) feAttachedKeys.add(k);
    const excludedFromBegin = new Set<string>(
      targetReport?.excludedFromBegin ?? [],
    );
    if (targetReport)
      for (const k of targetReport.feAttachedKeys) feAttachedKeys.add(k);
    const sd = maySamples ? toSampleDefaults(defaults) : null;
    const spatialish = (n: string) =>
      /\.(csv|zip|kmz|kml|geojson|json|shp)$/i.test(n);
    const invalidateField = (fieldId: string) => {
      void qc.invalidateQueries({ queryKey: qk.fields });
      void qc.invalidateQueries({ queryKey: qk.samples(fieldId) });
      void qc.invalidateQueries({ queryKey: qk.sampleEvents(fieldId) });
      void qc.invalidateQueries({ queryKey: qk.layers(fieldId) });
      void qc.invalidateQueries({ queryKey: qk.field(fieldId) });
    };
    // Target layers do not need the Samples tick: a design has no depth or extractant.
    if (!targetReport && files.some((f) => spatialish(f.name))) {
      try {
        const tscan = await tpl.scanTargetFiles(files, {
          skipKeys: sd ? completePairPointKeys(pairing) : undefined,
        });
        setPhase("attaching");
        const trep = await tpl.attachTargetsFast(tscan, {
          onProgress: (d, total, last) =>
            setAttaching({ done: d, total, last }),
          onAttached: invalidateField,
        });
        setTargetReport(trep);
        setTargetLeft([
          ...tscan.left,
          ...trep.toProcessing.map((t) => ({ file: t.file, reason: t.reason })),
        ]);
        for (const k of trep.feAttachedKeys) feAttachedKeys.add(k);
        for (const k of trep.excludedFromBegin) excludedFromBegin.add(k);
        setHandledSampleFiles([
          ...new Set([...handledSampleFiles, ...trep.feAttachedKeys]),
        ]);
      } catch {
        /* the chain remains the ingester of record */
      }
      setAttaching(null);
    }
    if (sd && !sampleReport && files.some((f) => spatialish(f.name))) {
      try {
        const sscan = await sfp.scanSampleFiles(files, sd.units, pairing, {
          matchByPointId,
        });
        setSampleLeft(sscan.left);
        if (sscan.handled.length) {
          setPhase("attaching");
          setAttaching({
            done: 0,
            total: sscan.handled.reduce((n, h) => n + h.groups.length, 0),
          });
          const rep = await sfp.attachSamplesFast(sscan, sd, {
            onProgress: (d, total, last) =>
              setAttaching({ done: d, total, last }),
            onAttached: invalidateField,
          });
          setSampleReport(rep);
          setSampleLeft([
            ...sscan.left,
            ...rep.rolledBack.map((r) => ({
              file: r.file,
              reason: `attach failed (${r.reason}) — sent to processing instead`,
            })),
          ]);
          for (const k of rep.feAttachedKeys) feAttachedKeys.add(k);
          setHandledSampleFiles([...feAttachedKeys]);
        }
      } catch {
        /* the chain remains the ingester of record */
      }
      setAttaching(null);
    }
    // ⚡ ZONE FAST PATH — mgmt / Rx / zone polygons via saveZones, then zone-results CSVs via SS3c
    if (!zoneReport) {
      try {
        const consumed = new Set(feAttachedKeys);
        for (const k of pairing.consumedKeys ?? []) consumed.add(k);
        const skipFiles = new Set(
          (scanNow?.candidates ?? []).map((c) => c.sourceFile.split("/")[0]!),
        );
        setPhase("attaching");
        const zscan = await zfp.scanZoneFiles(files, {
          skipFiles,
          skipped: scanNow?.skipped,
          consumedKeys: consumed,
        });
        setZoneLeft(zscan.left);
        let saved: import("../lib/zoneFastPath").SavedZoneSet[] = [];
        if (zscan.handled.length) {
          setAttaching({
            done: 0,
            total: zscan.handled.reduce((n, h) => n + h.groups.length, 0),
          });
          const zrep = await zfp.attachZonesFast(zscan, {
            onProgress: (d, total, last) =>
              setAttaching({ done: d, total, last }),
            onAttached: invalidateField,
          });
          setZoneReport(zrep);
          saved = zrep.saved;
          setZoneLeft([
            ...zscan.left,
            ...zrep.rolledBack.map((r) => ({
              file: r.file,
              reason: `attach failed (${r.reason}) — sent to processing instead`,
            })),
          ]);
          for (const k of zrep.feAttachedKeys) {
            feAttachedKeys.add(k);
            consumed.add(k);
          }
        }
        if (sd && !zoneResultReport) {
          for (const k of feAttachedKeys) consumed.add(k);
          const rscan = await zfp.scanZoneResultFiles(files, sd.units, {
            consumedKeys: consumed,
            saved,
          });
          setZoneLeft((left) => [...left, ...rscan.left]);
          if (rscan.handled.length) {
            const rrep = await zfp.attachZoneResultsFast(rscan, sd, {
              onAttached: invalidateField,
            });
            setZoneResultReport(rrep);
            setZoneLeft((left) => [
              ...left,
              ...rrep.rolledBack.map((r) => ({
                file: r.file,
                reason: `attach failed (${r.reason}) — sent to processing instead`,
              })),
            ]);
            for (const k of rrep.feAttachedKeys) feAttachedKeys.add(k);
          }
        }
      } catch {
        /* chain owns leftover zone files */
      }
      setAttaching(null);
    }
    // ⚡ THE YIELD LANE — after the field creates, so a harvest on a field created seconds ago
    // locates correctly. Sequential with the prompt because the grower's answer is part of the
    // attestation. Any failure here simply means no annotation: the chain ingests as it always has.
    let feYieldByKey:
      Map<string, { envelope: unknown; field_id?: string }> | undefined;
    // zip check FIRST so a non-harvest drop does not await before `:begin`.
    if (files.some((f) => spatialish(f.name))) {
      const { yieldAttachIsLive } = await import("../lib/yieldLaneLive");
      if (yieldAttachIsLive()) {
        try {
          const yfp = await import("../lib/yieldFastPath");
          setPhase("yield");
          let yscan = await yfp.scanYieldFiles(files);
          if (yscan.needsInfo.length) {
            const answers = await askYieldInfo(yscan.needsInfo);
            // no answer (skipped or cancelled) → an empty map, which sends those archives to
            // processing by the same rule an unanswered operation does
            yscan = yfp.completeWithUserInfo(yscan, answers ?? new Map());
          }
          setYieldReport(yscan);
          if (yscan.attested.length) {
            // Visualize is painted HERE, in this turn — not after:begin, not after a stack fetch.
            const prev = await import("../lib/yieldPreview");
            prev.showYieldAttestationPreviews(yscan.attested);
            feYieldByKey = new Map();
            for (const a of yscan.attested) {
              const top = a.envelope.operations
                .flatMap((o) => o.fields)
                .sort((x, y) => y.probes - x.probes)[0];
              const val = {
                envelope: a.envelope,
                ...(top ? { field_id: top.fieldId } : {}),
              };
              for (const k of a.fileKeys ?? [a.fileKey])
                feYieldByKey.set(k, val);
            }
          }
        } catch {
          /* the chain remains the ingester of record */
        }
      }
    }

    // THE IDENTIFICATION PASS (second screen) — kicked off AFTER the field creates, or its locate
    // races createFieldsFast and calls a brand-new field's own samples "not inside your fields"
    // (review); parallel with attach + upload from here. The scanSeq fence keeps an identification
    // of an EDITED-away manifest from publishing over the current one; a same-manifest retry keeps
    // the previous answer instead of flashing the skeleton.
    if (!ident) {
      const seq = scanSeqRef.current;
      void import("../features/drop-identification/dropIdentify")
        .then((m) => m.identifyDrop(files, pairing))
        .then((r) => {
          if (seq === scanSeqRef.current) setIdent(r);
        })
        .catch(() => {
          if (seq === scanSeqRef.current)
            setIdent({ items: [], attributionError: true });
        });
    }
    // `:begin` era: the chain is filed FIRST (inside uploadBulkDrop), THEN bytes upload behind this
    // progress state. If begin throws, NOTHING was uploaded anywhere -- that ordering is the whole
    // orphaned-byte design, so there is no separate 'starting' step after the upload any more.
    if (seq !== scanSeqRef.current) return;
    setPhase("uploading");
    try {
      const uploadFiles = files.filter(
        (f) => !excludedFromBegin.has(`${f.name}:${f.size}`),
      );
      if (uploadFiles.length === 0) {
        setPhase("started");
        return;
      }
      const res = await api.uploadBulkDrop(uploadFiles, {
        feAttachedKeys,
        feYieldByKey,
        onFileDone: () => setDone((n) => n + 1),
        sampleDefaults: maySamples
          ? (toSampleDefaults(defaults) ?? undefined)
          : undefined,
        sampleOptions: {
          may_contain_samples: maySamples,
          match_by_point_id: maySamples && matchByPointId,
        },
        feCreatedFieldIds,
      });
      setUploadFailed(res.failed);
      setSessionId(res.session_id);
      // only NOW are the annotations really filed — marking earlier would let a failed `:begin`
      // strand the attestation on a retry, and marking a file whose PUT then failed would send a
      // later re-drop unannotated (BE-Light rebound item 4)
      const filed = Object.keys(res.yieldJobs ?? {});
      setYieldFiledKeys(new Set(filed));
      const commits = res.yieldCommits ?? [];
      setYieldOnMap(
        commits.some((c) =>
          c.yield_attach?.fields.some(yieldCommitShouldRetire),
        ),
      );
      for (const c of commits) applyYieldCommit(qc, c);
      if (filed.length) {
        const yfp = await import("../lib/yieldFastPath");
        yfp.markAttested(filed);
      }
      setPhase("started");
    } catch (e) {
      setErr(
        e instanceof Error
          ? e.message
          : "Something went wrong starting this drop.",
      );
      setPhase("idle");
    }
  };

  // what the fast path did, shown once the session view takes over (and while uploading)
  const fastSummary =
    fastReport &&
    (fastReport.created.length ||
      fastReport.matchedExisting.length ||
      fastReport.failed.length) ? (
      <div className="rounded border border-emerald-500/40 bg-emerald-500/5 px-2 py-1.5 text-[11px]">
        <div className="font-medium text-fg">
          ⚡ {fastReport.created.length} field
          {fastReport.created.length === 1 ? "" : "s"} created instantly —
          already on your dashboard
        </div>
        {fastReport.created.length > 0 && (
          <div className="mt-0.5 text-[10px] text-muted">
            {fastReport.created.map((c) => c.field.field_name).join(" · ")}
          </div>
        )}
        {fastReport.matchedExisting.length > 0 && (
          <div className="mt-0.5 text-[10px] text-faint">
            {fastReport.matchedExisting
              .map((m) =>
                `${m.candidate.name} matches your existing field ${m.hits[0]?.field_name ?? ""}`.trim(),
              )
              .join(" · ")}{" "}
            — not re-created
          </div>
        )}
        {fastReport.failed.length > 0 && (
          <div className="mt-0.5 text-[10px] text-faint">
            {fastReport.failed.length} left for the server to create (
            {fastReport.failed.map((f) => f.candidate.name).join(", ")})
          </div>
        )}
      </div>
    ) : null;

  const askYieldInfo = (needs: YieldNeedsInfo[]) =>
    new Promise<Map<string, { crop: string; year: number }> | null>(
      (resolve) => {
        yieldResolveRef.current = resolve;
        setYieldAnswers({});
        setYieldPrompt(needs);
      },
    );
  const answerYield = (send: boolean) => {
    const resolve = yieldResolveRef.current;
    const needs = yieldPrompt;
    yieldResolveRef.current = null;
    setYieldPrompt(null);
    if (!resolve) return;
    if (!send) return resolve(null);
    const out = new Map<string, { crop: string; year: number }>();
    for (const n of needs ?? []) {
      const k = `${n.fileKey}|${n.member}`;
      const v = yieldAnswers[k] ?? { crop: "", year: "" };
      const year = Number(v.year);
      const yearOk = Number.isInteger(year) && year >= 1990 && year <= 2100;
      if (n.missing.includes("crop") && !v.crop) continue;
      if (n.missing.includes("date") && !yearOk) continue;
      out.set(k, {
        crop: v.crop || n.partial.crop || "",
        year: yearOk ? year : 1990,
      });
    }
    resolve(out);
  };

  // what the YIELD lane attested — and what it handed on, with the reason
  const filedAttested =
    yieldReport?.attested.filter((a) => yieldFiledKeys.has(a.fileKey)) ?? [];
  const yieldSummary =
    yieldReport && (yieldReport.attested.length || yieldReport.left.length) ? (
      <div className="rounded border border-emerald-500/40 bg-emerald-500/5 px-2 py-1.5 text-[11px]">
        {yieldReport.attested.length > 0 && (
          <>
            <div className="font-medium text-fg">
              ⚡ {yieldReport.attested.length} yield map
              {yieldReport.attested.length === 1 ? "" : "s"} read
              {filedAttested.length > 0 ? " and sent for fast import" : ""}
            </div>
            <div className="mt-0.5 text-[10px] text-muted">
              {yieldReport.attested.map((a) => a.summary).join(" · ")}
            </div>
            {filedAttested.length > 0 && (
              <div className="mt-0.5 text-[10px] text-faint">
                {yieldOnMap
                  ? "Your maps are on Visualize."
                  : "Your maps appear once processing finishes — minutes, not the usual wait."}
              </div>
            )}
          </>
        )}
        {yieldReport.left.length > 0 && (
          <div className="mt-0.5 text-[10px] text-faint">
            Left to processing:{" "}
            {yieldReport.left.map((l) => `${l.file} (${l.reason})`).join(" · ")}
          </div>
        )}
      </div>
    ) : null;

  // what the SAMPLE fast path did — attached-by-location results + what stayed with the server, and why
  const sampleSummary = sampleReport ? (
    <div className="rounded border border-emerald-500/40 bg-emerald-500/5 px-2 py-1.5 text-[11px]">
      {sampleReport && sampleReport.attached.length > 0 && (
        <>
          <div className="font-medium text-fg">
            ⚡ {sampleReport.attached.reduce((n, a) => n + a.points, 0)} soil
            samples attached instantly — placed by location
          </div>
          <div className="mt-0.5 text-[10px] text-muted">
            {sampleReport.attached
              .map(
                (a) =>
                  `${a.fieldName} (${a.points} pt${a.points === 1 ? "" : "s"})`,
              )
              .join(" · ")}
          </div>
        </>
      )}
      {sampleReport && sampleReport.failed.length > 0 && (
        <div className="mt-0.5 text-[10px] text-red-400">
          {sampleReport.failed
            .map((f) => `${f.file} · ${f.fieldName}: ${f.error}`)
            .join(" — ")}
        </div>
      )}
      {sampleReport && sampleReport.provenanceWarnings.length > 0 && (
        <div className="mt-0.5 text-[10px] text-amber-500">
          {sampleReport.provenanceWarnings.join(" · ")}
        </div>
      )}
    </div>
  ) : null;

  const zoneSummary =
    zoneReport?.attached.length || zoneResultReport?.attached.length ? (
      <div className="rounded border border-emerald-500/40 bg-emerald-500/5 px-2 py-1.5 text-[11px]">
        {zoneReport && zoneReport.attached.length > 0 && (
          <>
            <div className="font-medium text-fg">
              ⚡ {zoneReport.attached.reduce((n, a) => n + a.zones, 0)} zones
              saved instantly
            </div>
            <div className="mt-0.5 text-[10px] text-muted">
              {zoneReport.attached
                .map((a) => `${a.fieldName} (${a.zones})`)
                .join(" · ")}
            </div>
          </>
        )}
        {zoneResultReport && zoneResultReport.attached.length > 0 && (
          <div className="mt-0.5 text-[10px] text-muted">
            Zone results:{" "}
            {zoneResultReport.attached
              .map((a) => `${a.file} → ${a.fieldName}`)
              .join(" · ")}
          </div>
        )}
        {zoneResultReport && zoneResultReport.provenanceWarnings.length > 0 && (
          <div className="mt-0.5 text-[10px] text-amber-500">
            {zoneResultReport.provenanceWarnings.join(" · ")}
          </div>
        )}
      </div>
    ) : null;

  const busy =
    phase === "creating" ||
    phase === "attaching" ||
    phase === "yield" ||
    phase === "uploading" ||
    phase === "starting";

  // A RESUMED session was never identified in this browser — the session view is the whole screen.
  if (phase === "started" && sessionId && resumed) {
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-2">
        <BulkSessionView sessionId={sessionId} startedNow={false} />
      </div>
    );
  }

  // An error drops phase back to 'idle', so the manifest (with the error and a retryable button) is
  // never lost. THE OUTCOME: once the upload is in and identification has landed, the screen
  // resolves to ONE of two endings — a SUCCESS screen when every file was handled in real time, or
  // a HANDOFF list naming each file that still needs deeper analysis (and why) with "check back
  // later".
  if (phase !== "idle") {
    const handoff =
      phase === "started" && ident
        ? handoffFor({
            scan,
            fastReport,
            sampleReport,
            sampleLeft: [...sampleLeft, ...targetLeft],
            ident,
            maySamples,
            attestedYieldFiles: new Set(filedAttested.map((a) => a.file)),
            zoneReport,
            zoneResultReport,
            zoneLeft,
            alsoHandled: [
              ...(targetReport?.attached.map((a) => a.file) ?? []),
              ...(targetReport?.refused.map((r) => r.file) ?? []),
            ],
          })
        : null;
    const targetRefused = targetReport?.refused.length ?? 0;
    const success =
      handoff !== null &&
      handoff.length === 0 &&
      uploadFailed.length === 0 &&
      targetRefused === 0;
    const finish = () => {
      setFiles([]); // the manifest effect resets identification + the per-drop reports
      setSessionId(null);
      setHandledSampleFiles([]);
      setUploadFailed([]);
      setDone(0);
      setPhase("idle");
    };
    if (success) {
      return (
        <UploadSuccess
          fastReport={fastReport}
          sampleReport={sampleReport}
          zoneReport={zoneReport}
          zoneResultReport={zoneResultReport}
          targetReport={targetReport}
          yieldFiles={filedAttested.map((a) => a.file)}
          onDone={finish}
        />
      );
    }
    const progressLine =
      phase === "creating" && creating
        ? `Creating fields ${creating.done}/${creating.total}${creating.last ? ` — ${creating.last}` : ""}…`
        : phase === "attaching" && attaching
          ? `Attaching ${attaching.done}/${attaching.total}${attaching.last ? ` — ${attaching.last}` : ""}…`
          : phase === "yield"
            ? yieldPrompt
              ? null
              : "Reading yield maps…"
            : phase === "uploading"
              ? `Uploading ${done}/${files.length} files…`
              : null;
    return (
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
        <IdentificationCard scan={scan} fastReport={fastReport} ident={ident} />
        {progressLine && (
          <p className="flex items-center gap-1.5 text-[11px] text-muted">
            <Loader2 size={11} className="animate-spin" /> {progressLine}
          </p>
        )}
        {fastSummary}
        {sampleSummary}
        <TargetLaneNote report={targetReport} />
        {zoneSummary}
        {yieldSummary}
        {yieldPrompt && (
          <div className="rounded border border-amber-500/50 bg-amber-500/5 px-2 py-2 text-[11px]">
            <div className="font-medium text-fg">
              {yieldPrompt.every(
                (n) => (n.partial.opType ?? "harvest") === "harvest",
              )
                ? "What was harvested here?"
                : "When did this happen?"}
            </div>
            <p className="mt-0.5 text-[10px] text-muted">
              {yieldPrompt.length === 1 ? "This file came" : "These files came"}{" "}
              without a date
              {yieldPrompt.some((n) => n.missing.includes("crop"))
                ? " (and some don’t name the crop)"
                : ""}
              . Without those, the map can’t be used by recommendations — so
              we’re asking rather than importing something unusable.
            </p>
            <ul className="mt-1.5 space-y-1.5">
              {yieldPrompt.map((n) => {
                const key = `${n.fileKey}|${n.member}`;
                const a = yieldAnswers[key] ?? { crop: "", year: "" };
                const set = (patch: Partial<{ crop: string; year: string }>) =>
                  setYieldAnswers((cur) => ({
                    ...cur,
                    [key]: { ...a, ...patch },
                  }));
                const needCrop = n.missing.includes("crop");
                const needDate = n.missing.includes("date");
                return (
                  <li
                    key={key}
                    className="rounded border border-line px-1.5 py-1"
                  >
                    <div className="text-[10px] text-muted">
                      <span className="text-fg">{n.member || n.file}</span> ·{" "}
                      {n.partial.pointCount.toLocaleString()} points
                      {n.partial.opType && n.partial.opType !== "harvest"
                        ? ` · ${n.partial.opType}`
                        : ""}
                      {n.partial.fields.length
                        ? ` → ${n.partial.fields.map((f) => f.fieldName).join(" + ")}`
                        : ""}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5">
                      {needCrop && (
                        <select
                          aria-label={`Crop for ${n.member || n.file}`}
                          className="rounded border border-line bg-canvas px-1 py-0.5 text-[11px]"
                          value={a.crop}
                          onChange={(e) => set({ crop: e.target.value })}
                        >
                          <option value="">Crop…</option>
                          {[
                            "Corn",
                            "Soybeans",
                            "Wheat",
                            "Canola",
                            "Sorghum",
                          ].map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      )}
                      {needDate && (
                        <input
                          aria-label={`${needCrop ? "Harvest year" : "Year"} for ${n.member || n.file}`}
                          type="number"
                          placeholder="Year"
                          className="w-20 rounded border border-line bg-canvas px-1 py-0.5 text-[11px]"
                          value={a.year}
                          onChange={(e) => set({ year: e.target.value })}
                        />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="mt-1.5 flex items-center gap-2">
              <button
                className="rounded bg-accent px-2 py-1 text-[11px] font-medium text-canvas hover:bg-accent-hover"
                onClick={() => answerYield(true)}
              >
                Use these
              </button>
              <button
                className="text-[10px] text-faint hover:text-fg"
                onClick={() => answerYield(false)}
              >
                Skip — send these to processing
              </button>
            </div>
          </div>
        )}
        {uploadFailed.length > 0 && (
          <div className="rounded border border-red-500/40 bg-red-500/10 px-2 py-1.5 text-[11px]">
            <div className="font-medium">
              Didn’t reach us — not part of this drop
            </div>
            <ul className="mt-0.5">
              {uploadFailed.map((f) => (
                <li key={f.filename} className="text-faint">
                  <span className="text-fg">{f.filename}</span> — {f.error}
                </li>
              ))}
            </ul>
          </div>
        )}
        {handoff && handoff.length > 0 && (
          <div className="rounded border border-amber-500/40 bg-amber-500/5 px-2 py-1.5 text-[11px]">
            <div className="font-medium text-fg">
              Handed off for deeper analysis
            </div>
            <ul className="mt-0.5 space-y-0.5">
              {handoff.map((h) => (
                <li key={h.file} className="text-[10px] text-muted">
                  <span className="text-fg">{h.file}</span> —{" "}
                  {h.reasons.join("; ")}
                </li>
              ))}
            </ul>
            <p className="mt-1 text-[10px] text-faint">
              Check back later — this drop stays under “Drops in progress” on
              the upload screen, and anything that needs you lands in the Errors
              menu.
            </p>
          </div>
        )}
        {((handoff && handoff.length > 0) || targetRefused > 0) && (
          <button
            className="self-start rounded border border-line px-2 py-1 text-xs text-muted hover:bg-hover hover:text-fg"
            onClick={finish}
          >
            Done
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 sm:flex-row">
      <div
        className={`flex min-h-[14rem] flex-1 flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
          dragging ? "border-accent bg-accent/10" : "border-line"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragging(false);
          addFiles(e.dataTransfer.files);
        }} // stopPropagation: without it the drop BUBBLES to GlobalDropZone's window handler, which ran ingest-boundary.
      >
        <UploadCloud
          size={40}
          className={dragging ? "text-accent" : "text-faint"}
        />
        <p className="mt-3 text-base font-semibold text-fg">
          Drop Your Data Here
        </p>
        <p className="text-sm text-muted">We’ll Parse it For You</p>
        <p className="mt-2 max-w-sm text-[11px] text-faint">
          Boundaries, zones, soil samples, EC scans, yield/harvest exports — any
          mix. Shapefile (.zip), KML, KMZ, GeoJSON and more.
        </p>
        <button
          className="mt-3 rounded border border-line px-2 py-1 text-xs text-muted hover:bg-hover hover:text-fg"
          onClick={() => inputRef.current?.click()}
        >
          or browse
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            if (inputRef.current) inputRef.current.value = "";
          }}
        />

        {/* Resume: the session lives on the server, so a closed tab or a reload doesn't lose a drop. */}
        {recent && recent.length > 0 && (
          <div className="mt-4 w-full max-w-sm text-left">
            <div className="text-[10px] font-medium text-muted">
              Drops in progress
            </div>
            <ul className="mt-1 space-y-0.5">
              {recent.slice(0, 4).map((s) => (
                <li key={s.session_id}>
                  <button
                    className="w-full truncate rounded px-1 py-0.5 text-left text-[11px] text-muted hover:bg-hover hover:text-fg"
                    onClick={() => {
                      setResumed(true);
                      setSessionId(s.session_id);
                      setPhase("started");
                    }}
                  >
                    {s.object_count} object{s.object_count === 1 ? "" : "s"} ·{" "}
                    {s.file_count} file
                    {s.file_count === 1 ? "" : "s"} — {s.status}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex w-full flex-col rounded-lg border border-line sm:w-72">
        <div className="flex items-baseline justify-between border-b border-line px-2 py-1.5">
          <span className="text-[11px] font-medium">
            Files ({files.length})
          </span>
          {files.length > 0 && !busy && (
            <button
              className="text-[10px] text-faint hover:text-fg"
              onClick={() => setFiles([])}
            >
              clear
            </button>
          )}
        </div>
        {files.length === 0 ? (
          <p className="px-2 py-3 text-[11px] text-faint">
            Nothing dropped yet.
          </p>
        ) : (
          <ul className="max-h-64 min-h-0 flex-1 overflow-auto p-1">
            {files.map((f, i) => (
              <li
                key={`${f.name}:${f.size}:${i}`}
                className="flex items-center gap-1.5 rounded px-1 py-0.5 text-[11px] hover:bg-hover"
              >
                <FileText size={11} className="shrink-0 text-faint" />
                <span className="min-w-0 flex-1 truncate" title={f.name}>
                  {f.name}
                </span>
                <span className="shrink-0 tabular-nums text-[10px] text-faint">
                  {fmtBytes(f.size)}
                </span>
                {!busy && (
                  <button
                    className="shrink-0 rounded p-0.5 text-faint hover:text-red-400"
                    aria-label={`Remove ${f.name}`}
                    onClick={() =>
                      setFiles((cur) => cur.filter((_, j) => j !== i))
                    }
                  >
                    <Trash2 size={11} />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {files.length > 0 && (
          <div className="border-t border-line px-2 py-1.5 text-[10px] text-faint">
            {fmtBytes(totalBytes)} total
          </div>
        )}
        {err && (
          <div className="border-t border-line px-2 py-1.5 text-[10px] text-red-400">
            {err}
          </div>
        )}
        {/* a failed:begin must not hide REAL created fields OR attached samples behind a retryable error (review) */}
        {err ? (
          <div className="px-2 pb-1.5">
            {fastSummary}
            {sampleSummary}
          </div>
        ) : null}
        <BulkSampleDefaults
          maySamples={maySamples}
          setMaySamples={setMaySamples}
          matchByPointId={matchByPointId}
          setMatchByPointId={setMatchByPointId}
          defaults={defaults}
          setDefaults={setDefaults}
          hasFiles={files.length > 0}
          problem={defaultsProblem}
        />

        <div className="border-t border-line p-2">
          {/* ⚡ what the fast path FOUND, before the press — the promise the button then keeps */}
          {files.length > 0 &&
            (scanning ? (
              <p className="mb-1.5 flex items-center gap-1 text-[10px] text-faint">
                <Loader2 size={10} className="animate-spin" /> Checking for
                field boundaries…
              </p>
            ) : scan?.candidates.length ? (
              <p className="mb-1.5 text-[10px] text-emerald-500">
                ⚡ {scan.candidates.length} field boundar
                {scan.candidates.length === 1 ? "y" : "ies"} detected — created
                the moment you press, visible on your dashboard immediately.
              </p>
            ) : null)}
          <button
            className="flex w-full items-center justify-center gap-1.5 rounded bg-accent px-2 py-1.5 text-xs font-medium text-canvas hover:bg-accent-hover disabled:opacity-50"
            disabled={files.length === 0 || busy || defaultsProblem !== null}
            title={defaultsProblem ?? undefined}
            onClick={run}
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            Upload & process
          </button>
          <p className="mt-1.5 text-[10px] text-faint">
            Then: every spatial object is classified as {CLASS_LIST} and
            imported automatically — anything that can’t be imported is recorded
            for you to review.
          </p>
          {/* Compute: field builds start at press, GEE-gated — say the cost BEFORE commit neutrally (not a warning; it's the normal cost of soil data, same as Add Field × N). No precise ETA: the build gate is fleet-wide, so a countdown would be a lie. */}
          <p className="mt-1 text-[10px] text-faint">
            New fields start preparing their soil data right away — expect a few
            hours for a handful of fields, up to a day for dozens. You can keep
            working; maps fill in as they’re ready.
          </p>
        </div>
      </div>
    </div>
  );
}

export function BulkDumpPanel() {
  const mode = useUiStore((s) => s.bulkDump);
  const close = useUiStore((s) => s.closeBulkDump);
  if (!mode) return null;

  return (
    <Modal>
      <div
        className="fixed inset-0 z-panel flex items-center justify-center bg-black/40 p-4"
        onClick={close}
      >
        <div
          className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg border border-line bg-surface shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <h2 className="flex items-center gap-1.5 text-sm font-semibold">
              {mode === "jd" ? (
                <Tractor size={14} />
              ) : (
                <UploadCloud size={14} />
              )}
              Bulk Data Dump: {mode === "jd" ? "JD Ops" : "Upload"}
            </h2>
            <button
              className="rounded p-1 text-muted hover:bg-hover"
              onClick={close}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
          <div className="flex min-h-0 flex-1 flex-col p-3">
            {mode === "upload" ? <UploadFlow /> : <JdFlow />}
          </div>
        </div>
      </div>
    </Modal>
  );
}
